import { useCallback, useId, useRef, useState, type MouseEvent } from "react";
import { ClipboardCheck, MessagesSquare } from "lucide-react";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { cn } from "../ui/utils";
import { AppLink } from "../navigation/AppLink";
import { useLanguage } from "../../i18n/language-provider";
import { formatDateForDisplay } from "../../dateFormatting";
import { useChangeFlash } from "../../hooks/useChangeFlash";
import { usePopoverSide, type PopoverRoom } from "../../hooks/usePopoverSide";
import { routePath, type AppRoute } from "../../navigation/routes";
import type { StrategyHelpStatus } from "../../services/strategyHelpApi";
import { RenderFaultBoundary } from "../RenderFaultBoundary";
import { useAskOptivo } from "./AskOptivoProvider";
import { assistantChatCopy } from "./assistant-chat-copy";
import { strategyHelpCopy } from "./strategy-help-copy";

const DATE_FORMAT: Intl.DateTimeFormatOptions = { day: "numeric", month: "long" };

/**
 * The open rail's hint: 16rem wide at most, on the button, 8px from the
 * window's edge. It opens beside the button where that fits and under it
 * where it does not: in the phone's menu the rail is 16rem of a window that
 * may be 375, and held to the room beside the button alone the hint read as a
 * column 86px wide and twelve lines tall there (high contrast, 5 Oct 2026).
 * The button stands at the rail's foot, so under it Radix puts the hint over
 * it instead. The folded rail is 4rem and only ever beside the page, where
 * 16rem fits.
 */
const HINT_ROOM: PopoverRoom = { widthRem: 16, sideOffset: 0, collisionPadding: 8 };

interface AskOptivoRailProps {
    /** Icon-only, for the folded rail. */
    collapsed: boolean;
    /** Leave for a proposal's review page; the shell decides whether it is open. */
    onNavigate?: (route: AppRoute) => void;
    /** Said once the window is asked for here — the phone's menu folds away from in front of it. */
    onAsked?: () => void;
}

/**
 * "Ask Optivo", on every page.
 *
 * The rail is the only chrome that stands on all of them, so this is where
 * the product's one way to ask lives: a question answered by e-mail, or a
 * revenue study that comes back as settings to activate
 * (`StrategyHelpRequestDialog`, which carries the property and the strategy
 * with it). It replaced a mail address repeated on the strategy editor, the
 * wizard and the Prices page — a request sent from here says which property
 * it is about, which a mail from a client never did.
 *
 * The window it opens floats over the page and is owned by
 * `AskOptivoProvider`, not by this entry, so it outlives the rail folding and
 * the phone's menu closing. The entry is also where the window goes when it
 * is minimised: it says so under the button (a dot on the folded rail),
 * flashes as the window goes into it, and brings it back on the next click.
 * An answer of the live assistant that ends while the window is not open is
 * said the same way — the dot, the flash and "The Optivo assistant (AI)
 * answered" — until the window is opened again.
 *
 * Under the button it says what is already waiting, so a second click is a
 * decision rather than a doubt: a proposal to review (linked by its own
 * address, the page the e-mail's button opens), else a request still open.
 * A status that cannot be read costs the status and nothing else, and so does
 * one that cannot be drawn: the button reads nothing of the status, and what
 * does (`AskOptivoWaiting`) is drawn inside a boundary of its own. The way to
 * ask is the last thing a status the page was not built for should take.
 */
export function AskOptivoRail({ collapsed, onNavigate, onAsked }: AskOptivoRailProps) {
    const { t } = useLanguage();
    const copy = strategyHelpCopy(t);
    const chatCopy = assistantChatCopy(t);
    const { status, view, ask, minimizedCount, registerEntry, assistantAvailable, answered, answeredCount } = useAskOptivo();
    const ids = useId();
    const minimizedNoteId = `${ids}-minimized`;
    const minimized = view === "minimized";
    // The window went into the entry, or the assistant answered while it was
    // not open: either way there is something here to come back to.
    const marked = minimized || answered;
    const [hintOpen, setHintOpen] = useState(false);
    const flashing = useChangeFlash(`${minimizedCount}:${answeredCount}`);
    const note = answered ? chatCopy.answered : copy.windowMinimized;
    // Only while marked, and not while the tooltip is open: an open tooltip
    // describes its trigger as well, and it opens on the same sentence.
    const describedByNote = marked && !hintOpen ? { "aria-describedby": minimizedNoteId } : {};
    const markedTooltip = answered
        ? `${chatCopy.answered}. ${chatCopy.answeredHint}`
        : `${copy.windowMinimized} ${copy.windowRestoreHint}`;
    const hint = assistantAvailable ? copy.askHintChat : copy.askHint;

    // Registered for as long as the button is on the page, so the focus can
    // come back to it when the window leaves the screen; and held here, so
    // the hint can measure the room beside it.
    const unregister = useRef<(() => void) | null>(null);
    const entry = useRef<HTMLButtonElement | null>(null);
    const entryRef = useCallback((element: HTMLButtonElement | null) => {
        entry.current = element;
        unregister.current?.();
        unregister.current = element ? registerEntry(element) : null;
    }, [registerEntry]);
    const hintSide = usePopoverSide(entry, hintOpen, HINT_ROOM);

    const openWindow = (event: MouseEvent<HTMLButtonElement>) => {
        ask(event.currentTarget);
        onAsked?.();
    };

    // Nothing stands in for a status that cannot be drawn: nothing is what the
    // entry shows whenever the status cannot be read. Tried again with every
    // status that follows, the key being the status itself: it is read again
    // when the page or the property changes, and the next may well draw.
    const waiting = (
        <RenderFaultBoundary part="rail.askOptivo.waiting" retryKey={status} fallback={null}>
            <AskOptivoWaiting status={status} collapsed={collapsed} onNavigate={onNavigate} />
        </RenderFaultBoundary>
    );

    if (collapsed) {
        return (
            <div
                className={cn(
                    "relative py-2 flex flex-col items-center gap-1 border-t border-sidebar-border",
                    flashing && "flash-attention",
                )}
            >
                <Tooltip open={hintOpen} onOpenChange={setHintOpen}>
                    <TooltipTrigger asChild>
                        <Button
                            ref={entryRef}
                            variant="ghost"
                            size="icon"
                            onClick={openWindow}
                            aria-label={copy.askButton}
                            {...describedByNote}
                            className="relative text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        >
                            <MessagesSquare className="h-4 w-4" />
                            {marked && (
                                <span aria-hidden="true" className="absolute right-1.5 top-1.5 size-2 rounded-full bg-sidebar-primary" />
                            )}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[min(16rem,var(--radix-tooltip-content-available-width))]">
                        <p className="font-medium">{copy.askButton}</p>
                        <p>{marked ? markedTooltip : hint}</p>
                    </TooltipContent>
                </Tooltip>
                {marked && <span id={minimizedNoteId} className="sr-only">{note}</span>}
                {waiting}
            </div>
        );
    }

    return (
        <div className={cn("p-2 border-t border-sidebar-border space-y-1.5", flashing && "flash-attention")}>
            <Tooltip open={hintOpen} onOpenChange={setHintOpen}>
                <TooltipTrigger asChild>
                    <Button
                        ref={entryRef}
                        variant="ghost"
                        onClick={openWindow}
                        {...describedByNote}
                        // The hover colour is restated, as the folded button
                        // restates it: a ghost button turns its text to the
                        // page's own on hover, dark on this dark rail, and the
                        // label vanished under the pointer.
                        className="w-full justify-start gap-2 bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground"
                    >
                        <MessagesSquare className="h-4 w-4" aria-hidden="true" />
                        {copy.askButton}
                        {marked && (
                            <span aria-hidden="true" className="ml-auto size-2 shrink-0 rounded-full bg-sidebar-primary" />
                        )}
                    </Button>
                </TooltipTrigger>
                <TooltipContent
                    side={hintSide}
                    collisionPadding={HINT_ROOM.collisionPadding}
                    className="max-w-[min(16rem,var(--radix-tooltip-content-available-width))]"
                >
                    {marked ? markedTooltip : hint}
                </TooltipContent>
            </Tooltip>
            {marked && (
                <p id={minimizedNoteId} className="px-2 text-xs text-sidebar-foreground/70">{note}</p>
            )}
            {waiting}
        </div>
    );
}

interface AskOptivoWaitingProps {
    /** What is waiting with Optivo, as last read; null until read, or when it could not be. */
    status: StrategyHelpStatus | null;
    collapsed: boolean;
    onNavigate?: (route: AppRoute) => void;
}

/**
 * What is already waiting with Optivo, under the entry's button: a proposal
 * to review, else a request still open, else nothing.
 *
 * The one part of the entry drawn from what the server sent, and so the one
 * part a status the page was not built for can stop from drawing. It is its
 * own component so that such a fault is thrown here, inside the boundary the
 * entry draws it in, and the button above it stays.
 */
function AskOptivoWaiting({ status, collapsed, onNavigate }: AskOptivoWaitingProps) {
    const { t, locale } = useLanguage();
    const copy = strategyHelpCopy(t);
    const date = (value: string) => formatDateForDisplay(new Date(value), locale, DATE_FORMAT);
    const openRequest = status?.openRequest ?? null;
    const pendingProposal = status?.pendingProposal ?? null;
    const proposalRoute: AppRoute | null = pendingProposal
        ? { page: "strategies", proposalId: pendingProposal.id }
        : null;

    if (collapsed) {
        if (!proposalRoute) return null;
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        className="text-sidebar-primary hover:bg-sidebar-accent"
                    >
                        <AppLink
                            to={routePath(proposalRoute)}
                            onNavigate={onNavigate ? () => onNavigate(proposalRoute) : undefined}
                            aria-label={`${copy.proposalTitle}. ${copy.reviewProposal}`}
                        >
                            <ClipboardCheck className="h-4 w-4" />
                        </AppLink>
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[min(16rem,var(--radix-tooltip-content-available-width))]">{copy.proposalTitle}</TooltipContent>
            </Tooltip>
        );
    }

    if (proposalRoute && pendingProposal) {
        return (
            <AppLink
                to={routePath(proposalRoute)}
                onNavigate={onNavigate ? () => onNavigate(proposalRoute) : undefined}
                className="flex items-start gap-2 rounded-md px-2 py-1 text-xs text-sidebar-foreground hover:bg-sidebar-accent"
            >
                <ClipboardCheck className="mt-px size-3.5 shrink-0 text-sidebar-primary" aria-hidden="true" />
                <span className="min-w-0">
                    <span className="block">{copy.proposalTitle}</span>
                    <span className="block text-sidebar-foreground/70">
                        {date(pendingProposal.sentAt ?? pendingProposal.createdAt)} · {copy.reviewProposal}
                    </span>
                </span>
            </AppLink>
        );
    }

    if (!openRequest) return null;
    return (
        <p className="px-2 text-xs text-sidebar-foreground/70">
            {(openRequest.kind === "study" ? copy.openStudy : copy.openQuestion)
                .replace("{date}", date(openRequest.createdAt))}
        </p>
    );
}
